import type {
  RuleBoxAdditionReason,
  RuleBoxHistoryEntry,
  RuleBoxHistoryOutcome,
  RuleBoxHistoryTrigger,
  RuleBoxRemovalReason,
} from "@loot-os/api-contract";

/** The box editor's History tab, added only for a Shopify rule box. */
export const RULE_BOX_HISTORY_TAB = "history";

/** What set a refresh off, in the words its headline starts with. */
export const RULE_BOX_TRIGGER_LABELS: Record<RuleBoxHistoryTrigger, string> = {
  created: "Box created",
  rules_changed: "Rules changed",
  manual: "Refreshed on request",
  daily: "Daily refresh",
  value_change: "Price change picked up",
  stock_change: "Stock change",
  product_change: "Product change",
  renamed: "Renamed",
};

/** How a refresh ended, for its badge. */
export const RULE_BOX_OUTCOME_LABELS: Record<RuleBoxHistoryOutcome, string> = {
  published: "New version",
  unchanged: "No change",
  refused: "Refused",
};

/** Why a product left the drop table. */
export const RULE_BOX_REMOVAL_REASON_LABELS: Record<RuleBoxRemovalReason, string> = {
  out_of_stock: "out of stock",
  removed_from_shop: "deleted in Shopify",
  product_unavailable: "draft, archived or unfulfillable",
  no_longer_matches: "no longer matches the rules",
  below_min_return: "worth less than the minimum return",
  not_sampled: "left out to stay under the item limit",
};

/** Why a product joined the drop table. */
export const RULE_BOX_ADDITION_REASON_LABELS: Record<RuleBoxAdditionReason, string> = {
  new_product: "new product",
  back_in_stock: "back in stock",
  now_matches: "now matches the rules",
  now_above_min_return: "now clears the minimum return",
  sampled: "picked to stay under the item limit",
};

/** Who set a refresh off, when the entry names no one more precisely. */
export const RULE_BOX_ACTOR_LABELS: Record<RuleBoxHistoryEntry["actor"]["kind"], string> = {
  merchant: "Merchant",
  staff: "Staff",
  system: "Automatic",
};

/** Triggers whose headline names the event that set them off, "Stock change: Blue Hoodie". */
const NAMES_CAUSE: ReadonlySet<RuleBoxHistoryTrigger> = new Set([
  "stock_change",
  "product_change",
  "value_change",
  "renamed",
]);

/**
 * One line saying what happened: a refusal says the solver's message, "rules changed" says by
 * whom, and a webhook's trigger names the product it was about.
 */
export function ruleBoxHistoryHeadline(
  entry: Pick<RuleBoxHistoryEntry, "trigger" | "outcome" | "actor" | "cause" | "refusalMessage">,
): string {
  if (entry.outcome === "refused") {
    return `Refused: ${entry.refusalMessage ?? "the rules could not build a box"}`;
  }
  const label = RULE_BOX_TRIGGER_LABELS[entry.trigger];
  if (entry.trigger === "rules_changed") {
    return `${label} by ${entry.actor.kind === "system" ? "the system" : entry.actor.kind}`;
  }
  if (NAMES_CAUSE.has(entry.trigger) && entry.cause) return `${label}: ${entry.cause}`;
  return label;
}

/** The actor column: the entry's own label, else the kind in words. */
export function ruleBoxActorLabel(actor: RuleBoxHistoryEntry["actor"]): string {
  return actor.label ?? RULE_BOX_ACTOR_LABELS[actor.kind];
}

/** "v3 → v4", "v3" when the table stayed, "—" when there is no version on either side. */
export function ruleBoxVersionChange(
  entry: Pick<RuleBoxHistoryEntry, "fromVersion" | "toVersion">,
): string {
  const { fromVersion: from, toVersion: to } = entry;
  if (from === null && to === null) return "—";
  if (from === null) return `v${to}`;
  if (to === null || to === from) return `v${from}`;
  return `v${from} → v${to}`;
}

/** "12 → 14 (+2)", "12" when the count held, "—" when neither side is known. */
export function ruleBoxItemCountChange(
  entry: Pick<RuleBoxHistoryEntry, "itemCountFrom" | "itemCountTo">,
): string {
  const { itemCountFrom: from, itemCountTo: to } = entry;
  if (from === null && to === null) return "—";
  if (from === null) return String(to);
  if (to === null || to === from) return String(from);
  const delta = to - from;
  return `${from} → ${to} (${delta > 0 ? "+" : "−"}${Math.abs(delta)})`;
}

/**
 * The price column, given the formatter the page reads the currency through: "$9.99 → $10.99",
 * one amount when it held, "—" when neither side is known.
 */
export function ruleBoxPriceChange(
  entry: Pick<RuleBoxHistoryEntry, "priceFromCents" | "priceToCents">,
  format: (cents: number) => string,
): string {
  const { priceFromCents: from, priceToCents: to } = entry;
  if (from === null && to === null) return "—";
  if (from === null) return format(to ?? 0);
  if (to === null || to === from) return format(from);
  return `${format(from)} → ${format(to)}`;
}

/** Whether an entry has any product-level change to open. */
export function hasRuleBoxChanges(entry: Pick<RuleBoxHistoryEntry, "changes">): boolean {
  const { added, removed, revalued } = entry.changes;
  return added.length + removed.length + revalued.length > 0;
}
